import React, { useRef } from 'react';
import BEM from '../../bem';
import { ReactComponent as CheckSvg } from '../../images/selected-check.svg';
import { ReactComponent as DropdownChevronSvg } from '../../images/dropdown-chevron.svg';
import { ContextMenu, MenuItemList, MenuItem } from '../ContextMenu';
import { PRIORITY_LABEL } from 'js-sdk/src/types/Tasks';
import type { TaskPriority } from 'js-sdk/src/types/Tasks';

const classes = BEM.with('PrioritySelect');

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = (
  Object.keys(PRIORITY_LABEL) as TaskPriority[]
).map((value) => ({ value, label: PRIORITY_LABEL[value] }));

type PrioritySelectProps = {
  value: TaskPriority;
  onChange: (value: TaskPriority) => void;
  accessibilityMode?: boolean;
  buttonTestId?: string;
  buttonAriaLabelledBy?: string;
  borderless?: boolean;
};

const PrioritySelect: React.FC<PrioritySelectProps> = ({
  value,
  onChange,
  accessibilityMode,
  buttonTestId,
  buttonAriaLabelledBy,
  borderless,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const selectedIndex = PRIORITY_OPTIONS.findIndex((p) => p.value === value);
  const selected = PRIORITY_OPTIONS[selectedIndex];

  return (
    <div className={classes('')}>
      <ContextMenu
        event="click"
        offsetY={6}
        position="bottominnerleft"
        relativeTo={buttonRef.current}
        theme="vertical"
        accessibilityMode={accessibilityMode}
        menu={
          <MenuItemList
            className={classes('menu')}
            accessibilityMode={accessibilityMode}
            selectedOptionIndex={selectedIndex}
          >
            {PRIORITY_OPTIONS.map((option) => {
              const isSelected = option.value === value;
              return (
                <MenuItem
                  className={classes('menu-item', { selected: isSelected })}
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    if (accessibilityMode) buttonRef.current?.focus();
                  }}
                >
                  <span className={classes('priority-option')}>
                    <span className={classes('priority-dot', { [option.value]: true })} />
                    {option.label}
                  </span>
                  {isSelected && <CheckSvg className={classes('check')} />}
                </MenuItem>
              );
            })}
          </MenuItemList>
        }
      >
        <button
          aria-haspopup="listbox"
          aria-labelledby={buttonAriaLabelledBy}
          className={classes('priority-btn', { borderless })}
          data-test-id={buttonTestId}
          ref={buttonRef}
          type="button"
        >
          <span className={classes('priority-option')}>
            <span className={classes('priority-dot', { [value]: true })} />
            {selected.label}
          </span>
          <DropdownChevronSvg aria-hidden />
        </button>
      </ContextMenu>
    </div>
  );
};

export default PrioritySelect;
